import { FC } from "react";

import { Stack } from "@mui/material";

import { ChoiceCard } from "./ChoiceCard/ChoiceCard";
import { useCampaignLifeCycleChoice } from "./helpers/useCampaignLifeCycleChoice";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardDivider } from "@/dashboard-design-system/Divider";
import { FormStepContainer } from "@/features/LiveFormEditor/Steps/FormStepContainer";
import { Autorenew, Sparks, Success } from "@/icons/outlined";

const BRAND_NEW_PROGRAM = "brand-new-program";

export const TicketingLiveEditorStepCampaignLifeCycle: FC = () => {
  const { t } = useTranslate();
  const { form, campaigns, replacesTicketingId, selectChoice, handleSaveAndNext, handleExit } =
    useCampaignLifeCycleChoice();

  return (
    <FormStepContainer
      title={t("dashboard", "liveFormEditor.stepCampaignLifeCycle.title")}
      form={form}
      animatePreview={false}
      actionsProps={{
        onBack: handleExit,
        backButtonLabel: t("dashboard", "liveFormEditor.topBar.exit"),
        onNext: handleSaveAndNext,
        customNextButtonLabel: t("dashboard", "liveFormEditor.stepCampaignLifeCycle.continue"),
      }}
      data-test="campaign-life-cycle-step"
    >
      <Stack gap={2}>
        <ChoiceCard
          value={BRAND_NEW_PROGRAM}
          title={t("dashboard", "liveFormEditor.stepCampaignLifeCycle.brandNew.title")}
          description={t("dashboard", "liveFormEditor.stepCampaignLifeCycle.brandNew.description")}
          selected={!replacesTicketingId}
          onSelect={() => selectChoice(null)}
          data-test="campaign-life-cycle-brand-new"
        />
        <DashboardDivider />
        {campaigns.map((campaign) => (
          <ChoiceCard
            key={campaign.ticketingId}
            value={campaign.ticketingId}
            title={t("dashboard", "liveFormEditor.stepCampaignLifeCycle.replaces.title", {
              campaignTitle: campaign.title,
            })}
            tooltip={t("dashboard", "liveFormEditor.stepCampaignLifeCycle.replaces.tooltip", {
              count: campaign.autoRenewingMemberCount,
            })}
            selected={replacesTicketingId === campaign.ticketingId}
            details={
              replacesTicketingId === campaign.ticketingId
                ? [
                    {
                      icon: <Autorenew />,
                      text: t("dashboard", "liveFormEditor.stepCampaignLifeCycle.replaces.autoRenewal"),
                    },
                    {
                      icon: <Sparks />,
                      text: t("dashboard", "liveFormEditor.stepCampaignLifeCycle.replaces.redirect"),
                    },
                    {
                      icon: <Success />,
                      text: t("dashboard", "liveFormEditor.stepCampaignLifeCycle.replaces.end", {
                        campaignTitle: campaign.title,
                      }),
                    },
                  ]
                : undefined
            }
            onSelect={() => selectChoice(campaign.ticketingId)}
            data-test="campaign-life-cycle-replaces"
          />
        ))}
      </Stack>
    </FormStepContainer>
  );
};
